import { c as _c } from "react/compiler-runtime";
import * as RadixToggle from '@radix-ui/react-toggle';
import { cva, cx } from 'class-variance-authority';
import { forwardRef, useId, useRef } from 'react';
import { useTimeout } from 'usehooks-ts';
export type ToggleProps = {
  /** Accessible name for a switch that has no visible `label`. */
  'aria-label'?: string;
  autoFocus?: boolean;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  label?: string | React.ReactNode;
  hint?: string;
  labelPlacement?: 'left' | 'right';
  onChange?: (checked: boolean) => void;
  onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  size?: 'default' | 'compact';
  className?: string;
  fullWidth?: boolean;
};
const Toggle = forwardRef<HTMLButtonElement, ToggleProps>((t0, ref) => {
  const $ = _c(40);
  const {
    "aria-label": ariaLabel,
    autoFocus: t1,
    label,
    hint,
    labelPlacement: t2,
    checked,
    defaultChecked,
    disabled: t3,
    onChange,
    onBlur,
    size: t4,
    className,
    fullWidth: t5
  } = t0;
  const autoFocus = t1 === undefined ? false : t1;
  const labelPlacement = t2 === undefined ? "right" : t2;
  const disabled = t3 === undefined ? false : t3;
  const size = t4 === undefined ? "default" : t4;
  const fullWidth = t5 === undefined ? false : t5;
  const id = useId();
  const toggleRef = useRef(null);
  let t6;
  if ($[0] !== autoFocus) {
    t6 = () => {
      if (autoFocus) {
        toggleRef.current?.focus();
      }
    };
    $[0] = autoFocus;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  useTimeout(t6, 100);
  const t7 = !!hint;
  let t8;
  if ($[2] !== className || $[3] !== disabled || $[4] !== fullWidth || $[5] !== labelPlacement || $[6] !== t7) {
    t8 = toggleVariants({
      labelPlacement,
      hasHint: t7,
      className,
      disabled,
      fullWidth
    });
    $[2] = className;
    $[3] = disabled;
    $[4] = fullWidth;
    $[5] = labelPlacement;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      WebkitTapHighlightColor: "transparent"
    };
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== hint || $[10] !== id || $[11] !== label || $[12] !== size) {
    t10 = (label || hint) && <div className="flex flex-col flex-1 min-w-0">{label && <label htmlFor={id} className={cx(labelVariants({
        size
      }))}>{label}</label>}{hint && <div className={cx(hintVariants({
        size
      }))}>{hint}</div>}</div>;
    $[9] = hint;
    $[10] = id;
    $[11] = label;
    $[12] = size;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const t11 = checked ?? false;
  let t12;
  if ($[14] !== disabled || $[15] !== size || $[16] !== t11) {
    t12 = switchVariants({
      size,
      disabled,
      pressed: t11
    });
    $[14] = disabled;
    $[15] = size;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = checked ?? false;
  let t14;
  if ($[18] !== disabled || $[19] !== size || $[20] !== t13) {
    t14 = circleVariants({
      size,
      disabled,
      pressed: t13
    });
    $[18] = disabled;
    $[19] = size;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <circle cx="8" cy="8" r="8" fill="currentColor" />;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== t14) {
    t16 = <svg className={t14} viewBox="0 0 16 16">{t15}</svg>;
    $[23] = t14;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== t12 || $[26] !== t16) {
    t17 = <span className={t12}>{t16}</span>;
    $[25] = t12;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== ariaLabel || $[29] !== checked || $[30] !== defaultChecked || $[31] !== disabled || $[32] !== id || $[33] !== onBlur || $[34] !== onChange || $[35] !== ref || $[36] !== t10 || $[37] !== t17 || $[38] !== t8) {
    t18 = <RadixToggle.Root id={id} role="switch" aria-label={ariaLabel} disabled={disabled} defaultPressed={defaultChecked} pressed={checked} onPressedChange={onChange} onBlur={onBlur} className={t8} style={t9} ref={ref}>{t10}{t17}</RadixToggle.Root>;
    $[28] = ariaLabel;
    $[29] = checked;
    $[30] = defaultChecked;
    $[31] = disabled;
    $[32] = id;
    $[33] = onBlur;
    $[34] = onChange;
    $[35] = ref;
    $[36] = t10;
    $[37] = t17;
    $[38] = t8;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  return t18;
});
const toggleVariants = cva('gap-3 group flex', {
  variants: {
    fullWidth: {
      true: 'w-full',
      false: 'w-fit'
    },
    labelPlacement: {
      right: 'flex-row-reverse',
      left: 'flex-row'
    },
    // A label + hint stack is taller than the switch, so it top-aligns; a bare label centers against
    // it. Both must live here: Tailwind emits `items-center` before `items-start`, so an
    // `items-start` in the base classes would win over this variant rather than be overridden.
    hasHint: {
      false: 'items-center',
      true: 'items-start'
    },
    disabled: {
      true: '',
      false: 'cursor-pointer'
    },
    size: {
      compact: '',
      default: ''
    }
  }
});
const switchVariants = cva(['relative rounded-full border-2 border-solid transition-all duration-200 ease-in-out overflow-hidden', 'group-focus-visible:ring-2 ring-offset-2'], {
  variants: {
    size: {
      compact: 'w-8 h-5',
      default: 'w-9 h-6'
    },
    disabled: {
      true: 'pointer-events-none opacity-40',
      false: ''
    },
    pressed: {
      true: 'bg-accent border-transparent hover:opacity-90',
      false: 'border-primary bg-secondary hover:bg-hover-control'
    }
  },
  defaultVariants: {
    size: 'default',
    pressed: false
  }
});
const circleVariants = cva(['absolute transition-transform duration-200 left-0.5 top-1/2 -translate-y-1/2'], {
  variants: {
    size: {
      compact: 'w-3 h-3',
      default: 'w-4 h-4'
    },
    disabled: {
      true: 'opacity-100',
      false: ''
    },
    pressed: {
      true: 'translate-x-[13px] text-quinary',
      false: 'text-tertiary'
    }
  }
});
const labelVariants = cva('text-left text-primary', {
  variants: {
    size: {
      compact: 'text-body-2',
      default: 'text-body-2'
    }
  }
});
const hintVariants = cva('text-left text-tertiary break-words', {
  variants: {
    size: {
      compact: 'text-body-2',
      default: 'text-body-2'
    }
  }
});
export default Toggle;